import _                                                              from 'underscore';
import moment, { DurationInputArg1, Moment, MomentInput, unitOfTime } from 'moment';
import { CONSTANTS }                                                  from '../constants';
import { TIME_UNITS }                                                 from '../constants/TimeEnums';


export class TimeService {

    static displayDateFormat = CONSTANTS.FORMATTING.DATES.MONTHDAYYEAR_SLASHED;
    static shortDisplayDateFormat = CONSTANTS.FORMATTING.DATES.MONTHDAYYEAR_SLASHED_SHORT;
    static yearFirstDisplayDateFormat = CONSTANTS.FORMATTING.DATES.YEARMONTHDAY_MOMENT;
    static dateRangeYearQuarterFormat = CONSTANTS.FORMATTING.DATES.YEARQUARTER_MOMENT;
    static dateRangeTansmissionDelimiter = ',';
    static dateRangeDisplayDelimiter = ' - ';

    static noZone( m: Moment ){

        let newMoment = TimeService.displayDateToMoment( TimeService.asDisplayDate( m ) );
        let offset = newMoment.utcOffset();
        newMoment.utc().add( offset, 'm' );

        return newMoment;
    }

    static isISO( input: MomentInput ): boolean{

        return moment( input, moment.ISO_8601, true ).isValid()
    }

    static getDisplayDateFormat(){

        return TimeService.displayDateFormat;

    };

    static getShortDisplayDateFormat(){

        return TimeService.shortDisplayDateFormat;

    };

    static getYearFirstDisplayDateFormat(){

        return TimeService.yearFirstDisplayDateFormat;

    };

    static getTimestamp(){

        return moment().format( 'x' );

    };

    static asDisplayDate( momentObj: Moment ){

        return moment( momentObj ).format( TimeService.getDisplayDateFormat() );

    };

    static asShortDisplayDate( momentObj: Moment ){

        return moment( momentObj ).format( TimeService.getShortDisplayDateFormat() );

    };

    static asYearFirstDisplayDate( momentObj: Moment ){

        return moment( momentObj ).format( TimeService.getYearFirstDisplayDateFormat() );

    };

    static displayDateToMoment( displayDate: string ){

        return moment( displayDate, TimeService.getDisplayDateFormat() );

    };

    static localDateToMoment( localDate: string ){

        return moment( localDate, TimeService.getYearFirstDisplayDateFormat() );

    };

    static localDateToISO( localDate: string ){

        return TimeService.momentToISO( moment( localDate, TimeService.getYearFirstDisplayDateFormat() ) );

    };

    static localDateToFormat( localDate: string, format = TimeService.displayDateFormat ){

        return TimeService.localDateToMoment( localDate ).format( format );

    };

    static localDateToDisplayDate( localDate: string ){

        return TimeService.asDisplayDate( moment( localDate, TimeService.getYearFirstDisplayDateFormat() ) );

    };

    static displayDateToLocalDate( displayDate: string ){

        return TimeService.displayDateToMoment( displayDate ).format( TimeService.getYearFirstDisplayDateFormat() );

    };

    static dateObjectStringToLocalDate( dateObjectString: string ){

        return TimeService.dateObjectToMoment( new Date( dateObjectString ) ).format( TimeService.getYearFirstDisplayDateFormat() )

    };

    static yearFirstDisplayDateToMoment( displayDate: string ){

        return moment( displayDate, TimeService.getYearFirstDisplayDateFormat() );

    };

    static displayDateToISO( displayDate: string ){

        return TimeService.displayDateToMoment( displayDate ).toISOString();

    };

    static momentToISO( momentObj: Moment = null ){

        return moment.isMoment( momentObj ) ? momentObj.toISOString() : null;

    };

    static momentToLocalDate( momentObj: Moment = null ){

        return moment.isMoment( momentObj ) ? momentObj.format( TimeService.getYearFirstDisplayDateFormat() ) : null;

    };

    static ISOtoMoment( ISOdate: string | Moment = null, toLocal = false ){

        return moment( ISOdate ).utc( toLocal );

    };

    static dateObjectToMoment( date: Date ){

        return moment( date );

    };

    static ISOtoFormat( ISOdate: string | Moment = null, format = TimeService.displayDateFormat, toLocal = false ){

        return TimeService.ISOtoMoment( ISOdate, toLocal ).format( format );

    };

    static ISOtoDisplayDate( ISOdate: string | Moment = null, toLocal = false ){

        return TimeService.ISOtoFormat( ISOdate, TimeService.displayDateFormat, toLocal );

    };

    static convertDateFieldsToFormat( objectWithDateFields: any = {}, dateFields: Array<string> = [], formatFunc: ( fieldData: any ) => any = _.identity ){

        // return the task data to write
        dateFields.map( dateField => {
            if( objectWithDateFields[ dateField ] ){
                objectWithDateFields[ dateField ] = formatFunc( objectWithDateFields[ dateField ] )
            }
        } );

        return objectWithDateFields;
    }

    static hasTimeframeRange = ( currentTimeframeRange: Array<any>, requiredEntryCount = null ) => {

        const hasValues = !_.isEmpty( _.compact( currentTimeframeRange ) );

        if( !hasValues && requiredEntryCount ){

            return currentTimeframeRange.length >= requiredEntryCount;
        }

        return hasValues;
    }

    static buildTransmissionPayloadFromTimeframeRange = ( timeframeRange: Array<Moment> ) => {

        return timeframeRange.map( value => {
            const dateValue = TimeService.asDisplayDate( value );
            return ( moment.isMoment( value ) && value.isValid() ) ? dateValue : null;
        } ).join( TimeService.dateRangeTansmissionDelimiter )
    }

    static expandTransmissionPayloadForTimeframeRange = ( timeframeRangeString: string ) => {

        return timeframeRangeString.split( TimeService.dateRangeTansmissionDelimiter ).map( value => {
            const momentValue = TimeService.displayDateToMoment( value );
            return ( momentValue.isValid() ) ? momentValue : null;
        } )
    }

    static isTimeUnitWithinDay( unit: unitOfTime.DurationAs ){
        let units = [ "hour", "hours", "h", "minute", "minutes", "m", "second", "seconds", "s", "millisecond", "milliseconds", "ms" ]

        return units.includes( unit );
    }

    static timerangeOverlapsTimeframe( subjectStartDate: Moment | string, subjectEndDate: Moment | string, timeframeStartDate: Moment | string, timeframeEndDate: Moment | string ){

        const _subStart       = _.isString( subjectStartDate ) ? TimeService.ISOtoMoment( subjectStartDate ) : subjectStartDate,
              _subEnd         = _.isString( subjectEndDate ) ? TimeService.ISOtoMoment( subjectEndDate ) : subjectEndDate,
              _timeframeStart = _.isString( timeframeStartDate ) ? TimeService.ISOtoMoment( timeframeStartDate ) : timeframeStartDate,
              _timeframeEnd   = _.isString( timeframeEndDate ) ? TimeService.ISOtoMoment( timeframeEndDate ) : timeframeEndDate;


        if( _subStart && _subStart.isValid() && _subEnd && _subEnd.isValid() && _timeframeStart && _timeframeStart.isValid() && _timeframeEnd && _timeframeEnd.isValid() ){

            const isInsideStartDate = _subStart.isBetween( _timeframeStart, _timeframeEnd, null, '()' ),
                  isInsideEndDate   = _subEnd.isBetween( _timeframeStart, _timeframeEnd, null, '()' ),
                  spansTimeframe    = _subStart.isBefore( _timeframeStart ) && _subEnd.isAfter( _timeframeEnd ),
                  isWithinTimeframe = isInsideStartDate || isInsideEndDate || spansTimeframe;

            return isWithinTimeframe;
        }

        return false;
    }

    static getQuarterCount = ( startDate: Moment = moment(), endDate: Moment = moment() ) => {

        let count           = 0,
            nextQuarterDate = startDate.clone();

        while( nextQuarterDate.isBefore( endDate ) ){
            count++;
            nextQuarterDate.add( 1, 'quarter' );
        }

        return count;

    };

    static dateFromYearAndQuarter = ( year: number, quarter: number = 1 ) => {

        return moment( `${ year }-${ quarter }`, TimeService.dateRangeYearQuarterFormat );

    };

    static getDayDifferential = ( startDate: Moment = moment(), endDate: Moment = moment(), useNonNegative: boolean = true ) => {

        return TimeService.getUnitDifferential( 'days', startDate, endDate, useNonNegative );
    }

    static getMonthDifferential = ( startDate: Moment = moment(), endDate: Moment = moment(), useNonNegative: boolean = true ) => {

        return TimeService.getUnitDifferential( 'months', startDate, endDate, useNonNegative );
    }

    static getYearDifferential = ( startDate: Moment = moment(), endDate: Moment = moment(), useNonNegative: boolean = true ) => {

        return TimeService.getUnitDifferential( 'years', startDate, endDate, useNonNegative );
    }

    static getUnitDifferential = ( type: unitOfTime.Diff = 'days', startDate: Moment = moment(), endDate: Moment = moment(), useNonNegative: boolean = true ) => {

        let diff = startDate.diff( endDate, type );

        return useNonNegative ? Math.abs( diff ) : diff;
    }

    static getDatesWithinRange = ( startDate: Moment = moment(), endDate: Moment = moment(), timespanFreq: DurationInputArg1 = 1, timespanType: unitOfTime.DurationConstructor = 'month' ) => {

        let traverserDate = startDate.clone(),
            dates         = [];

        while( traverserDate.isSameOrBefore( endDate ) ){

            dates.push( traverserDate.clone() );
            traverserDate.add( timespanFreq, timespanType );
        }

        return dates;
    }

    static isDateUnitEqual = ( dateA: Moment = moment(), dateB: Moment = moment(), unit: unitOfTime.StartOf = 'days' ) => {
        return dateA.isSame( dateB, unit )
    }

    static getStartEndLimitorDates = ( dateObjects: { startTime: Moment, endTime: Moment }[] = [ { startTime: moment(), endTime: moment() } ] ): [ Moment | null, Moment | null ] => {

        let startTime: Moment = null,
            endTime: Moment   = null;

        _.map( dateObjects, dateObject => {

            let activityStartDate = dateObject.startTime,
                activityEndDate   = dateObject.endTime;

            if( !startTime || activityStartDate.isBefore( startTime ) ){
                startTime = activityStartDate
            }
            if( !endTime || activityEndDate.isAfter( endTime ) ){
                endTime = activityEndDate
            }
        } );

        return [ startTime, endTime ];
    }

    static getGroupsOfDatesByRange = ( start: Moment, end: Moment, groupKey: unitOfTime.All | ( ( m: Moment ) => string ), partitionKey: unitOfTime.DurationConstructor = 'days' ) => {

        let arr = TimeService.getRangeOfDates( start, end, partitionKey );

        return _.groupBy( arr, ( date: Moment ) => _.isFunction( groupKey ) ? groupKey( date ) : date.get( groupKey ) );
    }

    static getRangeOfDates = ( start: Moment, end: Moment, key: unitOfTime.DurationConstructor, arr: Moment[] = [ start.startOf( key ) ] ): Moment[] => {

        if( start.isAfter( end ) ) throw new Error( '[Timeservice.getRangeOfDates]: Start must precede end' )

        const next = moment( start ).add( 1, key ).startOf( key );

        if( next.isAfter( end, key ) ) return arr;

        return TimeService.getRangeOfDates( next, end, key, arr.concat( next ) );

    }

    static getYearArrayFromDates = ( timestamps: Moment[] ) => {

        // upper / lower bounds of chart as years
        const lowerBoundYear = moment.min( timestamps ).year(),
              upperBoundYear = moment.max( timestamps ).year(),
              years          = _.range( lowerBoundYear, upperBoundYear + 1 );

        return years;
    }

    static getReoccuringDateSet = ( startDate: Moment, freqValue: number, freqUnit: unitOfTime.Diff, endDate: Moment = null ) => {

        let upperBoundsDate = endDate ?? startDate.clone().add( 2, 'years' ),
            monthFreqValues = _.range( freqValue, upperBoundsDate.diff( startDate, freqUnit ), freqValue );

        return monthFreqValues.map( ( freq, index ) => {

            return startDate.clone().add( freq, freqUnit );
        } )
    }

    static getHumanizedDays = (
        /* The timestamp to assess from now (unless specified with fromTimestamp) */
        timestamp: Moment | string     = moment(),
        /* A relative timestamp to assess from */
        fromTimestamp: Moment | string = moment(),
        /* Convert the timestamp from ISO */
        convertISO: boolean            = false,
        /* Type of language to use, non-relative removes the 'in' when humanizing, etc. */
        nonRelativeLanguage            = false,
        /* How close to compare timestamps */
        granularity: unitOfTime.Diff   = 'days',
    ) => {

        const toDate = ( convertISO && TimeService.isISO( timestamp ) )
                       ? TimeService.ISOtoMoment( timestamp as string ) as Moment
                       : timestamp as Moment;

        return moment.duration( toDate.diff( fromTimestamp, granularity ), granularity ).humanize( !nonRelativeLanguage, { d: 7, w: 4 } )
    }

    static getHumanizedDateRangeDiffInfo = (
        /* The timestamp to assess from now (unless specified with fromTimestamp) */
        timestamp: Moment | string      = moment(),
        /* A relative timestamp to assess from */
        fromTimestamp: Moment | string  = moment(),
        /* Convert the timestamp from ISO */
        convertISO: boolean             = false,
        /* Units to include in the string */
        displayUnits: Array<TIME_UNITS> = [ TIME_UNITS.DAYS, TIME_UNITS.MONTHS, TIME_UNITS.YEARS ]
    ): {
        string: string,
        parts: Partial<Record<TIME_UNITS, { value: number, name: string }>>,
        totalDays: number,
    } => {

        try{
            const toDate = ( convertISO && TimeService.isISO( timestamp ) )
                           ? TimeService.ISOtoMoment( timestamp as string ) as Moment
                           : timestamp as Moment;

            let totalDays = toDate.diff( fromTimestamp, 'days' );

            return {
                ...TimeService.getHumanizedDurationString( totalDays, displayUnits ),
                totalDays: totalDays,
            };

        } catch( e ) {

            // ...
        }

        return {
            string:    '',
            parts:     {},
            totalDays: -1,
        }
    }

    static getHumanizedDurationString = (
        /* Length in days of the duration */
        durationInDays: number,
        /* Units to include in the string */
        displayUnits: Array<TIME_UNITS> = [ TIME_UNITS.DAYS, TIME_UNITS.MONTHS, TIME_UNITS.YEARS ],
    ): {
        string: string,
        parts: Partial<Record<TIME_UNITS, { value: number, name: string }>>,
    } => {

        try{

            let duration              = moment.duration( durationInDays, 'days' ),
                durationStringBuilder = [],
                durationPartsBuilder  = {};

            const builderMap = new Map<TIME_UNITS, 'years' | 'months' | 'weeks' | 'days'>( [
                [ TIME_UNITS.YEARS, 'years' ],
                [ TIME_UNITS.MONTHS, 'months' ],
                [ TIME_UNITS.WEEKS, 'weeks' ],
                [ TIME_UNITS.DAYS, 'days' ],
            ] );

            Array.from( builderMap ).map( ( [ unit, durationUnitOfTime ] ) => {

                if( displayUnits.includes( unit ) ){

                    const value = duration[ durationUnitOfTime ]();

                    if( value > 0 ){

                        durationStringBuilder.push( `${ value } ${ TimeService.getHumanizedTimeUnits( value, unit ) }` );
                        durationPartsBuilder[ unit ] = { value, name: durationUnitOfTime }

                        // There is an issue when subtracting a duration of x months from a duration of y months,
                        // where the result is not the difference in months, but the difference in days and the day
                        // count could be added up to 30 days. In some cases, if we have 91 days, and the months reported was 2,
                        // the days will result in 30. If we subtract, the duration derives 1 month, not 30 days.
                        //
                        // NOT subtracting leaves us with a duration of 2 month and 30 days, which is more accurate.
                        //
                        // duration.subtract( value, durationUnitOfTime );
                    }
                }

            } );

            return {
                string: durationStringBuilder.join( ', ' ),
                parts:  durationPartsBuilder,
            };

        } catch( e ) {

            // ...
        }

        return {
            string: '',
            parts:  {},
        }
    }

    static getHumanizedTimeUnits = ( numUnits: number, unit: TIME_UNITS ) => {

        let labelHash = {
            [ TIME_UNITS.DAYS ]:   [ 'Day', 'Days' ],
            [ TIME_UNITS.WEEKS ]:  [ 'Week', 'Weeks' ],
            [ TIME_UNITS.MONTHS ]: [ 'Month', 'Months' ],
            [ TIME_UNITS.YEARS ]:  [ 'Year', 'Years' ],
        }

        return labelHash[ unit ]
               ? labelHash[ unit ][ ( numUnits < 2 ) ? 0 : 1 ]
               : `${ numUnits } ${ unit }`
    }
}
